/**
 * The six destinations, in rail order. Data rather than markup so the rail and the mobile tab bar
 * share one source of truth — the handoff ships them as separate components, but they must never
 * disagree about what exists or what is active.
 *
 * Note the handoff's Rail.dc.html carries a stale id enum (browse/queue/decks). These are the real
 * ids; using the stale ones highlights nothing.
 *
 * **文法 IS a slot, and this comment used to say the opposite** (日本-519). PRD §11 was resolved as
 * "one search, two result kinds" and 日本-498 built it that way — a second kind on a surface that
 * already existed rather than a seventh destination. Measured afterwards, that is what buries it:
 * on しまう the DoJG row is #6 of 70 at y=1494px, below the fold, on the exact construction the
 * feature was built for. So grammar left 検索 and became the reference module it always was.
 *
 * **Mining was a sixth and is gone** (Alex, 8 Aug 2026) — it is being removed from the designs, so
 * the slot goes with it rather than lingering as a stub. The rail's argument for listing unbuilt
 * destinations is that the IA is fixed and a destination appearing later would move every slot
 * beneath it; that argument works the same way in reverse, which is why this is a deletion now
 * rather than a `ready: false` row kept for tidiness. This used to name `mining_deck` in the server
 * config as its "one remaining trace"; that went on 16 Aug 2026, along with the seed script's deck
 * and the tray agent's `Anki:MiningDeck` — the deck rename is what proved nothing read any of them.
 */
export type DestinationId =
  | "review"
  | "read"
  | "cards"
  | "stats"
  | "groups"
  | "search"
  | "grammar"
  | "setup";

export interface Destination {
  id: DestinationId;
  /**
   * The destination, as a **word** — set vertically in the rail and horizontally in the tab bar.
   *
   * It was a single JP-serif glyph (`復 読 札 計 設`) with an English caption beneath it, which is
   * the shape an icon rail wants. Tategaki removes the need: a 44px column set
   * `writing-mode: vertical-rl` holds two characters comfortably, so the label *is* the icon and
   * there is nothing to caption. That is also what made the mark change necessary — 読 was the
   * app's mark and 読書 is the second nav item, which put the same character in two roles a few
   * rows apart. See `docs/ICONS.md`.
   */
  word: string;
  /**
   * English, and it is not drawn anywhere.
   *
   * It survives as the `title` and the accessible name, which is the one place English is still
   * right: a screen reader announcing `読書` is announcing the word, and `Read` is what the
   * destination is called in every other artefact — the URL's `?tab=read`, `DestinationId`, this
   * repo's prose.
   */
  label: string;
  /** Split surfaces get a list/log region above a detail dock; the rest are unsplit. */
  split: boolean;
  /** Built in this pass, or a stub for a later one. */
  ready: boolean;
}

export const DESTINATIONS: readonly Destination[] = [
  { id: "review", word: "復習", label: "Review", split: false, ready: true },
  { id: "read", word: "読書", label: "Read", split: true, ready: true },
  { id: "cards", word: "単語", label: "Cards", split: true, ready: true },
  { id: "stats", word: "統計", label: "Stats", split: false, ready: true },
  // Confusion groups (日本-601). **After 統計, and the position is `MORE_IDS`' rule again**: the
  // canvas drew it beside 単語, but there it would land on the phone bar and make six targets. At
  // the head of the tail it sits in the sheet with 検索, 文法 and 設定.
  { id: "groups", word: "混同", label: "Groups", split: false, ready: true },
  // **After 統計 rather than beside 単語, and the position is the RULE rather than a ranking**
  // (日本-497): the phone bar is this list minus `MORE_IDS`, so where 検索 sits decides whether it
  // is on the bar or in the sheet. Alex's IA call put it in the sheet beside 設定, which is only
  // expressible if the two are adjacent and last.
  { id: "search", word: "検索", label: "Search", split: false, ready: true },
  // **Directly below 検索** (Alex, 30 Aug 2026), which is what puts it in the phone's sheet rather
  // than on the bar: `MORE_IDS` is the TAIL of this list, so position is the whole statement.
  { id: "grammar", word: "文法", label: "Grammar", split: false, ready: true },
  { id: "setup", word: "設定", label: "Setup", split: false, ready: true },
] as const;

/**
 * What the phone's More sheet holds — **the tail of `DESTINATIONS`, and that is the whole rule**
 * (日本-497).
 *
 * A phone bar fits five targets. **Eight destinations since 日本-601**, so the sheet holds four; it
 * held three after 日本-519 and two before that, and a sheet of Stats/Setup/Grammar before Mining's removal left five and it
 * was deleted on 8 Aug 2026. Read the note on `TAB_BAR_IDS` below for what that deletion was really about, because
 * it is the thing this must not undo.
 *
 * Expressed as the **tail** rather than as a hand-written list so the bar and the sheet cannot
 * disagree about what exists: every destination is in exactly one of them, by construction, and
 * moving 検索 up the rail moves it onto the bar in the same edit. `destinations.test.ts` pins the
 * partition.
 */
export const MORE_IDS: readonly DestinationId[] = ["groups", "search", "grammar", "setup"];

/**
 * The phone tab bar: **every destination `MORE_IDS` does not claim, plus a その他 button.**
 *
 * The sheet is back and the thing that made the last one bad is not. What 8 Aug 2026 actually
 * deleted was `more` as a **tab that was not a place** — `destination()` threw on it, it could not
 * be serialized, and `lastRealTab` existed to hold where the URL pointed while it was open. None of
 * that returns: **`NavState.tab` stays a real `DestinationId` at all times**, その他 is an
 * `OverlayId` like `dialog` and `picker` (see `navHistory.ts`), and the bar lights it by asking
 * whether the *active destination* is one of `MORE_IDS`. There is no `more` tab value anywhere.
 *
 * The one thing genuinely given back is the failed-jobs badge, which returns to その他 from 設定 —
 * the deletion called that out as an improvement and it was right to. `TabBar` draws it on both the
 * button and the sheet's 設定 row, so the count is never only behind a tap.
 */
export const TAB_BAR_IDS: readonly DestinationId[] = DESTINATIONS.map((d) => d.id).filter(
  (id) => !MORE_IDS.includes(id),
);

export function destination(id: DestinationId): Destination {
  const found = DESTINATIONS.find((d) => d.id === id);
  if (!found) throw new Error(`unknown destination ${id}`);
  return found;
}
